import React, { useEffect } from "react";
import ImageGallery, {
  ImageGalleryImage,
} from "../../../components/ImageGallery";
import { useTranslation } from "react-i18next";
import { useGetApartmentDetailsQuery } from "../../../api/apartment/apartmentApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setApartmentDetails } from "../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { setAssetGroup } from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { formatCurrency } from "../../../util";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { ServiceTypeSelectorOption } from "../../../types/order";
import useServiceStorage from "../../../hooks/useServiceStorage";

const ApartmentOverview = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { selectedApartment } = useSelector(
    (state: RootState) => state.apartment
  );

  const { data, isLoading } = useGetApartmentDetailsQuery({
    apartmentId: selectedApartment?.assetId ?? "",
  });

  const { currentService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.APARTMENT
  );

  useEffect(() => {
    if (data) {
      dispatch(setApartmentDetails(data));
      dispatch(setAssetGroup({ id: data.groupId }));
    }
  }, [data, dispatch]);

  return (
    <div className="max-w-full mx-auto flex flex-col md:max-w-[80%] p-2">
      <div className="w-full">
        <ImageGallery
          images={
            (data?.additionalImages || []).map((src) => ({
              src,
            })) as ImageGalleryImage[]
          }
          aspectRatio="16/9"
          className="border border-gray-200"
        />
      </div>

      <div className="mt-2 text-xs">
        <h1 className="font-semibold text-lg mb-2 text-foreground">
          {data?.name}
        </h1>

        <div className="flex justify-between items-center mb-4 text-xs">
          <div className="flex flex-wrap gap-x-2 font-semibold text-xs text-gray-600">
            <>
              <span>
                {data?.accomodationdetail?.guests} {t("venue_reservation.guests")}
              </span>
              <span>
                • {data?.accomodationdetail?.rooms}{" "}
                {t("apartment.room", {
                  count: data?.accomodationdetail?.rooms ?? 1,
                })}
                {currentService === ServiceTypeSelectorOption.VENUE && (
                  <>
                    {" "}({data?.accomodationdetail?.sqm} {t("apartment.sqm")}){" "}
                  </>
                )}
              </span>
              {currentService !== ServiceTypeSelectorOption.VENUE && (
                <>
                  <span>
                    • {data?.accomodationdetail?.beds} {t("venue_reservation.beds")}
                  </span>
                  <span>
                    • {data?.accomodationdetail?.baths} {t("venue_reservation.baths")}
                  </span>
                </>
              )}
            </>

            {currentService === ServiceTypeSelectorOption.VENUE && (
              <>
                {(data?.accomodationdetail?.kitchen ?? 0) > 0 && (
                  <span>
                    • {data?.accomodationdetail?.kitchen} {t("apartment.kitchen")}
                  </span>
                )}

                {(data?.accomodationdetail?.terrace ?? 0) > 0 && (
                  <span>
                    • {data?.accomodationdetail?.terrace} {t("apartment.terrace")}
                  </span>
                )}

                {(data?.accomodationdetail?.garden ?? 0) > 0 && (
                  <span>
                    • {data?.accomodationdetail?.garden} {t("apartment.garden")}
                  </span>
                )}

                {(data?.accomodationdetail?.terrace ?? 0) === 0 &&
                  (data?.accomodationdetail?.garden ?? 0) === 0 && (
                    <span>
                      • {data?.accomodationdetail?.outdoor_space}{" "}
                      {t("apartment.no_outdoor_space")}
                    </span>
                  )}
              </>
            )}
          </div>
        </div>

        {/* Daily and Weekend Price Section */}
        {data?.Group?.pricecategory?.pricescale && (
          <div className="mb-4 text-sm text-gray-700">
            {(() => {
              const daily = data.Group.pricecategory.pricescale.find(
                (p) => p.days === "weekday"
              );
              const weekend = data.Group.pricecategory.pricescale.find(
                (p) => p.days === "weekend"
              );

              const isPriceOnRequest =
                (daily?.price ?? 0) <= 0 || (weekend?.price ?? 0) <= 0;

              return (
                <>
                  {isPriceOnRequest ? (
                    <div>
                      <span className="font-bold">
                        {t("apartment.price_on_request")}
                      </span>
                    </div>
                  ) : (
                    <>
                      {daily && daily.price > 0 && (
                        <div>
                          <span className="font-normal">
                            {t(
                              "book_location.venue_reservation_overview.daily_price"
                            )}
                            :{" "}
                          </span>
                          <span className="font-bold">
                            {t("general.from")} {formatCurrency(daily.price, "de-DE")}
                          </span>
                          <span className="text-gray-500 font-normal">
                            {" "}(
                            {t(
                              "book_location.venue_reservation_overview.daily_time_range"
                            )}
                            )
                          </span>
                        </div>
                      )}
                      {weekend && weekend.price > 0 && (
                        <div>
                          <span className="font-normal">
                            {t(
                              "book_location.venue_reservation_overview.weekend_price"
                            )}
                            :{" "}
                          </span>
                          <span className="font-bold">
                            {t("general.from")} {formatCurrency(weekend.price, "de-DE")}
                          </span>
                          <span className="text-gray-500 font-normal">
                            {" "}(
                            {t(
                              "book_location.venue_reservation_overview.weekend_time_range"
                            )}
                            )
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        <h2 className="font-bold text-foreground text-sm leading-6 mb-1">
          {t("book_location.venue_reservation_overview.our_facilities")}
        </h2>
        <p className="text-gray-700 mb-3 font-normal text-sm leading-5">
          {data?.accomodationdetail?.facilityDescription}
        </p>

        {(() => {
          const note = data?.accomodationdetail?.importantNote;
          return typeof note === "string" && note.length > 0 ? (
            <>
              <h3 className="font-bold text-foreground text-sm leading-6 mb-1">
                {t(
                  "book_location.venue_reservation_overview.important_guidelines"
                )}
              </h3>
              <p className="text-gray-700 mb-3 font-normal text-sm leading-5">
                {note}
              </p>
            </>
          ) : null;
        })()}

        <h3 className="font-bold text-foreground text-sm leading-6 mb-1">
          {t("apartment.location")}
        </h3>
        <div className="w-full h-[200px] border border-gray-200 rounded-lg overflow-hidden mb-3">
          <iframe
            title="Property Location"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={`https://www.google.com/maps?q=${data?.Group?.location?.address?.geoLat},${data?.Group?.location?.address?.geoLng}&z=15&output=embed`}
            allowFullScreen
          />
        </div>
      </div>
      <LoaderOverlay open={isLoading} />
    </div>
  );
};

export default ApartmentOverview;
